// climate_admin_panel\src\pages\Events\ImportEventDialog.jsx

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Box, Typography } from '@mui/material'; // Added LinearProgress, Box, Typography
import { useState } from 'react';
import FileImport from './FileImport';
import apiFunctions from '../../api/apiFunctions';
import { toast } from 'react-toastify';
import { describeImportProgress } from '../../utils/importProgressStream';

const ImportEventDialog = ({ open, handleClose }) => {
    const [importPayload, setImportPayload] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [totalChunks, setTotalChunks] = useState(0);
    const [currentChunk, setCurrentChunk] = useState(0);
    const [progressLabel, setProgressLabel] = useState('');
    const [progressDetail, setProgressDetail] = useState('');

    const data = importPayload?.rows || [];
    const excelFile = importPayload?.type === 'excel' ? importPayload.file : null;

    const applyProgressEvent = (event, chunkIndex = 0, numChunks = 1) => {
        const { label, detail } = describeImportProgress(event);
        setProgressLabel(label);
        setProgressDetail(detail);

        const chunkWeight = 100 / numChunks;
        const base = chunkIndex * chunkWeight;
        const chunkPercent = event?.percent ?? (event?.phase === 'done' ? 100 : 0);
        setUploadProgress(Math.min(100, Math.round(base + (chunkPercent / 100) * chunkWeight)));
    };

    const handleFileLoad = (payload) => {
        setImportPayload(payload);
        setIsUploading(false);
        setUploadProgress(0);
        setTotalChunks(0);
        setCurrentChunk(0);
        setProgressLabel('');
        setProgressDetail('');
    };

    const importExcelFile = async () => {
        if (!excelFile) {
            toast.warn("No Excel file selected.");
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);
        setProgressLabel('Starting import...');
        setProgressDetail('');

        try {
            const finalEvent = await apiFunctions.importEventsExcelStream(excelFile, (event) => {
                applyProgressEvent(event);
            });

            if (finalEvent?.phase === 'error' || finalEvent?.status === false) {
                toast.error(finalEvent?.message || 'Excel import failed');
                return;
            }

            const imported = finalEvent?.totalImported ?? 0;
            const skipped = finalEvent?.skipped ?? 0;
            toast.success(
                skipped > 0
                    ? `Imported ${imported} events (${skipped} row(s) skipped).`
                    : `Imported ${imported} events successfully.`
            );
            setImportPayload(null);
            handleClose();
        } catch (error) {
            console.error("Excel import failed:", error);
            toast.error(error.message || 'Excel import failed');
        } finally {
            setIsUploading(false);
            setUploadProgress(100);
        }
    };

    const createEventsInChunks = async () => {
        if (excelFile) {
            await importExcelFile();
            return;
        }

        if (data.length === 0) {
            toast.warn("No data to import.");
            return;
        }

        setIsUploading(true); // Start upload indication
        setUploadProgress(0);

        const CHUNK_SIZE = 1000; // Or 100, 200 etc. Adjust based on testing what size works reliably
        const totalEvents = data.length;
        const numChunks = Math.ceil(totalEvents / CHUNK_SIZE);
        setTotalChunks(numChunks);
        setCurrentChunk(0);

        let successfulImports = 0;
        let failedChunks = 0;

        for (let i = 0; i < numChunks; i++) {
            const start = i * CHUNK_SIZE;
            const end = start + CHUNK_SIZE;
            const chunk = data.slice(start, end);
            setCurrentChunk(i + 1); // Update current chunk number (1-based for display)

            try {
                const finalEvent = await apiFunctions.importEventsStream(
                    { events: chunk, isBulkAdd: true },
                    (event) => applyProgressEvent(event, i, numChunks)
                );

                if (finalEvent?.phase === 'error' || finalEvent?.status === false) {
                    toast.error(`Error importing chunk ${i + 1}: ${finalEvent?.message || 'Unknown error'}`);
                    failedChunks++;
                    continue;
                }

                const insertedCount = typeof finalEvent?.inserted === 'number'
                    ? finalEvent.inserted
                    : chunk.length;
                successfulImports += insertedCount;
            } catch (error) {
                // Handle network errors or unexpected issues
                console.error("Error during chunk upload:", error);
                // Check for specific error types if needed (e.g., CORS, network down)
                if (error.message?.includes('CORS')) {
                    toast.error("CORS error: Please check server configuration.", { autoClose: 5000 });
                } else if (error.code === 'ERR_NETWORK') {
                    toast.error(`Network error during chunk ${i + 1} upload. Please check connection.`);
                } else {
                    toast.error(`Failed to upload chunk ${i + 1}. Error: ${error.message}`);
                }
                failedChunks++;
                // Keep going through the rest of the file even if one chunk
                // fails, so every record gets a chance to import. Failures are
                // counted and reported in the summary toast at the end.
                continue;
            }

            // Update progress after each chunk attempt
            setUploadProgress(Math.round(((i + 1) / numChunks) * 100));
        }

        // Upload finished (or stopped due to error)
        setIsUploading(false);

        if (failedChunks === 0) {
            toast.success(`All ${totalEvents} events imported successfully in ${numChunks} chunks!`);
            setImportPayload(null);
            handleClose();
        } else {
            toast.warn(`Import finished with ${failedChunks} failed chunks. ${successfulImports} events might have been imported.`);
            // Optionally: Don't clear data or close dialog on partial failure
        }
    };


    return (
        <Dialog
            PaperProps={{
                sx: { width: "80%", maxWidth: "900px" }
            }} onClose={handleClose} open={open} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">
                {"Import special events"}
            </DialogTitle>
            <DialogContent>
                {/* Hide file input during upload */}
                {!isUploading && <FileImport onFileLoad={handleFileLoad} />}

                {/* Show progress bar during upload */}
                {isUploading && (
                    <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
                        <Typography variant="body1" align="center">
                            {excelFile
                                ? (progressLabel || 'Uploading Excel file...')
                                : `Chunk ${currentChunk} of ${totalChunks}: ${progressLabel || 'Importing...'}`}
                        </Typography>
                        {progressDetail && (
                            <Typography variant="body2" align="center" color="text.secondary">
                                {progressDetail}
                            </Typography>
                        )}
                        <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />
                        <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                            {uploadProgress}%
                        </Typography>
                    </Box>
                )}

                {/* Optionally: Preview data (consider performance for large sets) */}
                {excelFile && !isUploading && (
                    <Typography sx={{ mt: 2 }}>
                        Excel file selected: <strong>{excelFile.name}</strong>. Rows will be parsed on the server.
                    </Typography>
                )}

                {data.length > 0 && !isUploading && (
                    <>
                        <Typography sx={{ mt: 2, mb: 1 }}>Previewing first {Math.min(data.length, 5)} events of {data.length} total:</Typography>
                        {/* // Simplified preview - keep the table small or remove if too slow? */}
                        <div className="table-responsive table-bordered" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            <table className="table">
                                {/* Keep table header */}
                                <thead>
                                    <tr>
                                        <th scope="col">source_id</th>
                                        <th scope="col">title</th>
                                        <th scope="col">latitude</th>
                                        <th scope="col">longitude</th>
                                        {/* Add other relevant columns for preview */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Show only a few rows */}
                                    {data.slice(0, 5).map((item, index) => <tr key={index}>
                                        <td>{item.source_id}</td>
                                        <td>{item.title}</td>
                                        <td>{item.latitude}</td>
                                        <td>{item.longitude}</td>
                                        {/* Add other relevant cells */}
                                    </tr>)}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {/* Show Save button only if data is loaded and not currently uploading */}
                {(data.length > 0 || excelFile) && !isUploading && (
                    <Button onClick={createEventsInChunks} variant="contained">
                        {excelFile ? `Import ${excelFile.name}` : `Import ${data.length} Events`}
                    </Button>
                )}
                {/* Show Cancel/Close button */}
                <Button onClick={handleClose} disabled={isUploading}>
                    {isUploading ? "Uploading..." : "Cancel"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ImportEventDialog;


// // climate_admin_panel\src\pages\Events\ImportEventDialog.jsx

// import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
// import React, { useState } from 'react'
// import FileImport from './FileImport'
// import apiFunctions from '../../api/apiFunctions';
// import { useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';

// const ImportEventDialog = ({ open, handleClose }) => {

//     const [data, setData] = useState([]);

//     const handleFileLoad = (csvData) => {
//         setData(csvData);
//     };

//     const navigate = useNavigate();

//     const createEvents = async () => {
//         // console.log("hellogetUserDeatil")
//         const result = await apiFunctions.importEvents({ events: data });
//         console.log(result, "result22222222222222222222222")
//         if (!result.data.status) {
//             if (result.data.code === 401) {
//                 localStorage.clear();
//                 navigate("/login")
//                 toast.info("Session Expired Please login again!!")
//             }
//             toast.error(result.data.message, {
//                 position: toast.POSITION.TOP_RIGHT
//             });

//         } else {
//             toast.success("Events imported successfully");
//             setData([])
//             handleClose();
//         }
//     }


//     return (
//         <Dialog
//             PaperProps={{
//                 sx: { width: "80%", maxWidth: "900px" } // Adjust as needed
//             }} onClose={handleClose} open={open} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
//             <DialogTitle id="alert-dialog-title">
//                 {"Import special events"}
//             </DialogTitle>
//             <DialogContent>
//                 <FileImport onFileLoad={handleFileLoad} />
//             </DialogContent>
//             <DialogActions>
//                 {data.length > 0 && <div className="table-responsive table-bordered">
//                     <table className="table">
//                         <thead>
//                             <tr>
//                                 <th scope="col">source_id</th>
//                                 <th scope="col">title</th>
//                                 <th scope="col">latitude</th>
//                                 <th scope="col">longitude</th>
//                                 <th scope="col">map_url</th>
//                                 <th scope="col">place</th>
//                                 <th scope="col">country</th>
//                                 <th scope="col">country_code</th>
//                                 <th scope="col">state_code</th>
//                                 <th scope="col">world_rank</th>
//                                 <th scope="col">required_people</th>
//                                 <th scope="col">live_expected_people</th>
//                                 <th scope="col">annual_emission_of_co2</th>
//                                 {/* <th scope="col">event_type</th> */}
//                                 {/* <th scope="col">tag</th> */}
//                                 <th scope="col">sector_rank</th>
//                                 <th scope="col">special_notes</th>
//                             </tr>
//                         </thead>
//                         <tbody>
//                             {data.map((item, index) => <tr key={index}>
//                                 <td scope="col">{item.source_id}</td>
//                                 <td scope="col">{item.title}</td>
//                                 <td scope="col">{item.latitude}</td>
//                                 <td scope="col">{item.longitude}</td>
//                                 <td scope="col">{item.map_url}</td>
//                                 <td scope="col">{item.place}</td>
//                                 <td scope="col">{item.country}</td>
//                                 <td scope="col">{item.country_code}</td>
//                                 <td scope="col">{item.state_code}</td>
//                                 <td scope="col">{item.world_rank}</td>
//                                 <td scope="col">{item.required_people}</td>
//                                 <td scope="col">{item.live_expected_people}</td>
//                                 <td scope="col">{item.annual_emission_of_co2}</td>
//                                 {/* <td scope="col">{item.event_type}</td> */}
//                                 {/* <td scope="col">{item.tag}</td> */}
//                                 <td scope="col">{item.sector_rank}</td>
//                                 <td scope="col">{item.special_notes}</td>
//                             </tr>)}
//                         </tbody>
//                     </table>
//                     <Button onClick={() => { createEvents() }}>Save Events</Button>
//                 </div>}
//                 {/* <Button onClick={() => { removeEvent(id) }} >Yes</Button> */}
//             </DialogActions>
//         </Dialog>
//     )
// }

// export default ImportEventDialog